import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { MessageCircle, MessageSquarePlus, Save as SaveIcon, SlidersHorizontal, UserRound } from 'lucide-react';
import {
  architectApi, approvalErrorText, CommentAnchor, PricingMethodology, PricingMonthlyRow, PricingPlan,
  PricingSheet, PricingSheetResponse,
} from '../../architect/api';
import { anchorQuote, anchorRevision, commentsAtBlock, CommentPinRef } from '../../architect/CommentAnchors';
import {
  DEFAULT_RULES, casePricingEnabled, caseSaleTotal, invalidatePricingSections, pricingSectionConfirmed,
} from '../../architect/pricingMath';
import MonthlyServicesEditor from '../../architect/MonthlyServicesEditor';
import { monthlyRowsReady } from '../../architect/monthlyReviewState';
import { proposalApi } from '../../architect/proposalApi';
import { useAuth } from '../../auth/AuthContext';
import { stageAccess } from '../../architect/stageAccess';
import ApproveAsSelector, { useApprovalActor } from '../../architect/ApproveAsSelector';
import { architectureReviewView } from '../../architect/architectureReview';
import { useMainButton, haptic } from '../telegram';
import { Sheet } from '../components/Sheet';
import { PricingNotice } from '../components/PricingNotice';
import { TmaMonthlyRecommendation } from './TmaMonthlyRecommendation';
import PricingReviewFlow, { PricingReviewStep, PRICING_REVIEW_STEPS } from '../../architect/PricingReviewFlow';
import './tma-pricing-workspace.css';
import { StageHeader, StageFooter, StageAiButton } from '../../architect/StageChrome';
import './tma-pricing-mobile.css';

export interface PricingTabProps {
  projectId: string;
  active: boolean;
  onPipelineChange: () => void | Promise<void>;
  onDraftStateChange?: (state: { dirty: boolean; busy: boolean }) => void;
  onCommentAnchor?: (anchor: CommentAnchor) => void;
  commentPins?: CommentPinRef[];
  onOpenCommentThread?: (id: string) => void;
  pickingComment?: boolean;
  jumpAnchor?: CommentAnchor | null;
  commentsOpen?: boolean;
}

const EMPTY: PricingSheet = { setup: [], monthly: [] };


export function PricingTab({ projectId, active, onPipelineChange, onDraftStateChange, onCommentAnchor, commentPins = [], onOpenCommentThread, pickingComment, jumpAnchor, commentsOpen }: PricingTabProps) {
  const { user, permissions = [] } = useAuth();
  const access = stageAccess(user, permissions, 'pricing');
  const accessRef = useRef(access); accessRef.current = access;
  const [step, setStep] = useState<PricingReviewStep>('development');
  const [monthlyVisited, setMonthlyVisited] = useState(false);
  const [suggestionsProject, setSuggestionsProject] = useState<string | null>(null);
  const suggestionsOpen = suggestionsProject === projectId;
  const setSuggestionsOpen = (value: boolean) => setSuggestionsProject(value ? projectId : null);
  const [monthlySourceKey, setMonthlySourceKey] = useState('');
  const [sourceOpen, setSourceOpen] = useState(false);
  const [highlightPath, setHighlightPath] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [meta, setMeta] = useState<PricingSheetResponse | null>(null);
  const [sheet, setSheetState] = useState<PricingSheet>(EMPTY);
  const editRevision = useRef(0);
  const loadedProject = useRef<string | null>(null);
  const loadRequest = useRef(0);
  const currentProject = useRef(projectId);
  currentProject.current = projectId;
  const setSheet = useCallback<React.Dispatch<React.SetStateAction<PricingSheet>>>((update) => {
    if (!accessRef.current.edit) return;
    editRevision.current += 1;
    setSheetState(update);
  }, []);
  const [methodology, setMethodology] = useState<PricingMethodology | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [changesOpen, setChangesOpen] = useState(false);
  const [changesReason, setChangesReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [recoveringProposal, setRecoveringProposal] = useState(false);
  const [repriceFailure, setRepriceFailure] = useState<string | null>(null);
  const [recoveryError, setRecoveryError] = useState<string | null>(null);
  const recoveryPending = useRef(false);
  const [savingBasis, setSavingBasis] = useState(false);
  const [assistantBusy, setAssistantBusy] = useState(false);
  const [basisOpen, setBasisOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [approvalSettingsOpen, setApprovalSettingsOpen] = useState(false);
  const pricingActorOptions = useMemo(() => meta?.approvals?.approvers || (meta?.assigned_to
    ? [{ id: meta.assigned_to, username: meta.assigned_to_name || 'Assigned approver', display_name: meta.assigned_to_name || null }]
    : []), [meta?.approvals?.approvers, meta?.assigned_to, meta?.assigned_to_name]);
  const pricingActor = useApprovalActor({ root: !!user?.isRoot, status: meta?.approvals, step: 'pricing',
    targetId: projectId, options: pricingActorOptions, enabled: meta?.status !== 'approved' });
  const [dayRate, setDayRate] = useState('600');
  const [monthlyPct, setMonthlyPct] = useState('10');
  const [middleRate, setMiddleRate] = useState(String(DEFAULT_RULES.middle_week_rate));
  const [seniorRate, setSeniorRate] = useState(String(DEFAULT_RULES.senior_week_rate));
  const [saveConflict, setSaveConflict] = useState(false);
  const [confirmReload, setConfirmReload] = useState(false);
  const [banner, setBanner] = useState<{ text: string; kind: 'ok' | 'err' } | null>(null);
  const savedSnapshot = useRef<string | null>(null);
  const savedBasisSnapshot = useRef<string | null>(null);
  const sheetRef = useRef(sheet);
  sheetRef.current = sheet;
  const draftStateCallback = useRef(onDraftStateChange);
  draftStateCallback.current = onDraftStateChange;
  const basisSnapshot = JSON.stringify([dayRate, monthlyPct, middleRate, seniorRate]);
  const projectReady = loadedProject.current === projectId;
  const sheetDirty = projectReady && savedSnapshot.current !== null && JSON.stringify(sheet) !== savedSnapshot.current;
  const basisDirty = projectReady && savedBasisSnapshot.current !== null && basisSnapshot !== savedBasisSnapshot.current;
  const dirty = sheetDirty || basisDirty;
  const busy = loading || saving || savingBasis || assistantBusy || approving || submitting || rejecting || recoveringProposal;
  const pricingRevision = anchorRevision(sheet);

  useLayoutEffect(() => {
    if (!active || !projectReady || loading) return;
    // Scroll the real document pane: scrollIntoView can leave this step behind
    // the sticky project header when the next decision has a different height.
    const scroller = rootRef.current?.closest<HTMLElement>('.tma-project-body');
    if (scroller?.scrollTo) scroller.scrollTo({ top: 0, behavior: 'auto' });
    else if (scroller) scroller.scrollTop = 0;
    else rootRef.current?.scrollIntoView?.({ block: 'start', behavior: 'auto' });
  }, [step, active, projectReady, loading]);

  useEffect(() => { draftStateCallback.current?.({ dirty, busy }); }, [dirty, busy]);
  useEffect(() => () => {
    draftStateCallback.current?.({ dirty: false, busy: false });
  }, []);

  useEffect(() => {
    setRepriceFailure(null);
    setRecoveryError(null);
    setApprovalSettingsOpen(false);
  }, [projectId]);

  useEffect(() => {
    if (!active || loading || !jumpAnchor || jumpAnchor.kind !== 'block' || jumpAnchor.document_id !== projectId) return;
    const match = /^(setup|monthly)\.(\d+)$/.exec(jumpAnchor.path || '');
    if (!match) return;
    const targetStep = match[1] === 'setup' ? 'development' : 'monthly';
    setStep(targetStep);
    if (targetStep === 'monthly') { setMonthlyVisited(true); } else setSourceOpen(true);
    const focusTimer = window.setTimeout(() => {
      const rows = Array.from(rootRef.current?.querySelectorAll<HTMLElement>('[data-tma-pr-anchor]') ?? []);
      const exact = rows.find(row => row.dataset.tmaPrAnchor === jumpAnchor.path);
      const target = exact?.dataset.tmaPrLabel === jumpAnchor.label ? exact
        : rows.find(row => row.dataset.tmaPrLabel === jumpAnchor.label && row.dataset.tmaPrAnchor?.startsWith(`${match[1]}.`)) ?? exact;
      if (!target) return;
      setHighlightPath(target.dataset.tmaPrAnchor || null);
      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView?.({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
      target.focus({ preventScroll: true });
    }, 60);
    const clearTimer = window.setTimeout(() => setHighlightPath(null), 2200);
    return () => { window.clearTimeout(focusTimer); window.clearTimeout(clearTimer); };
  }, [active, loading, jumpAnchor, projectId]);

  const rules = methodology?.rules ?? DEFAULT_RULES;
  const plan = useMemo<PricingPlan>(() => sheet.plan ?? { mode: 'sequential', qa_weeks: 0, qa_engineers: 1, qa_level: 'middle' }, [sheet.plan]);

  const load = useCallback(async (quiet = false) => {
    const request = ++loadRequest.current;
    const revision = editRevision.current;
    if (!quiet) setLoading(true);
    try {
      const r = await architectApi.getPricing(projectId);
      if (request !== loadRequest.current || revision !== editRevision.current || currentProject.current !== projectId) return;
      loadedProject.current = projectId;
      setMeta(r);
      setMonthlySourceKey(`${r.estimate_id || ''}:${r.source_reviewed_at || ''}`);
      const s = r.sheet || EMPTY;
      const prepared = { ...s, plan: s.plan ?? { mode: 'sequential' as const, qa_weeks: 0, qa_engineers: 1, qa_level: 'middle' as const } };
      savedSnapshot.current = JSON.stringify(prepared);
      setSheetState(prepared);
      if (r.methodology) {
        setMethodology(r.methodology);
        const values = [String(r.methodology.day_rate), String(r.methodology.monthly_pct),
          String(r.methodology.rules?.middle_week_rate ?? DEFAULT_RULES.middle_week_rate),
          String(r.methodology.rules?.senior_week_rate ?? DEFAULT_RULES.senior_week_rate)];
        savedBasisSnapshot.current = JSON.stringify(values);
        setDayRate(values[0]); setMonthlyPct(values[1]); setMiddleRate(values[2]); setSeniorRate(values[3]);
      }
      setBanner(null);
      setSaveConflict(false);
      setConfirmReload(false);
    } catch (err) {
      if (request === loadRequest.current && currentProject.current === projectId) {
        setBanner({ text: (err as Error).message || 'Could not load pricing', kind: 'err' });
      }
    } finally {
      if (!quiet && request === loadRequest.current && currentProject.current === projectId) setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (active && access.view) void load();
    return () => { loadRequest.current += 1; };
  }, [active, access.view, load]);

  // Refresh another reviewer's vote without replacing an unsaved draft or
  // unmounting the focused field. load also fences edits made in flight.
  useEffect(() => {
    if (!active || !access.view || meta?.status !== 'review' || dirty || busy) return undefined;
    const timer = window.setInterval(() => { void load(true); }, 20000);
    return () => window.clearInterval(timer);
  }, [active, access.view, meta?.status, dirty, busy, load]);


  const setMonthly = (i: number, patch: Partial<PricingMonthlyRow>) =>
    setSheet((p) => invalidatePricingSections({ ...p, monthly: p.monthly.map((r, j) => (j === i ? { ...r, ...patch } : r)) }, 'monthly'));
  const removeMonthly = (i: number) => { haptic.impact('light'); setSheet((p) => invalidatePricingSections({ ...p, monthly: p.monthly.filter((_, j) => j !== i) }, 'monthly')); };
  const addCustom = (preset?: PricingMonthlyRow) => { haptic.impact('light'); setSheet((p) => invalidatePricingSections({ ...p, monthly: [...p.monthly, preset || { label: '', description: '', model: 'fixed', amount: null, price: null, overage: null }] }, 'monthly')); };

  const commentOn = (path: string, label: string, quote: string) => {
    if (!onCommentAnchor && !onOpenCommentThread) return null;
    const anchor: CommentAnchor = {
      kind: 'block', document_id: projectId, revision: pricingRevision,
      path, label: label.slice(0, 160), quote: anchorQuote(quote),
    };
    const matches = commentsAtBlock(commentPins, anchor);
    if (!pickingComment && !matches.length) return null;
    return <button type="button" className={'tma-comment-pin' + (pickingComment ? ' is-picking' : ' has-comments')}
      aria-label={pickingComment ? `Comment on ${label}` : `Open comments on ${label}`}
      onClick={() => { if (!pickingComment && matches.length && onOpenCommentThread) onOpenCommentThread(matches[0].id); else onCommentAnchor?.(anchor); }}>
      {pickingComment ? <MessageSquarePlus size={17} strokeWidth={1.7} /> : <><MessageCircle size={17} /><span className="tma-comment-pin-count">{matches.length}</span></>}</button>;
  };

  const applyRecommendation = (rows: PricingMonthlyRow[], expectedSheet: string) => {
    if (!access.edit || JSON.stringify(sheetRef.current) !== expectedSheet) return false;
    setSuggestionsOpen(false);
    setSheet(current => invalidatePricingSections({ ...current, monthly: rows }, 'monthly'));
    return true;
  };



  const cancelBasis = () => {
    const values = savedBasisSnapshot.current ? JSON.parse(savedBasisSnapshot.current) as string[] : null;
    if (values) { setDayRate(values[0]); setMonthlyPct(values[1]); setMiddleRate(values[2]); setSeniorRate(values[3]); }
    setBasisOpen(false);
  };

  const editBasis = (setter: React.Dispatch<React.SetStateAction<string>>, value: string) => {
    // Rate edits are a draft too: an older review poll must not replace them.
    editRevision.current += 1;
    setter(value);
  };

  const saveBasis = async () => {
    if (!user?.isRoot || savingBasis || assistantBusy) return;
    const rate = Number(dayRate), pct = Number(monthlyPct), middle = Number(middleRate), senior = Number(seniorRate);
    if ([dayRate, monthlyPct, middleRate, seniorRate].some(value => !value.trim()) ||
      !Number.isFinite(rate) || rate < 0 || rate > 100_000 ||
      !Number.isFinite(pct) || pct < 0 || pct > 100 ||
      !Number.isFinite(middle) || middle < 0 || !Number.isFinite(senior) || senior < 0) {
      setBanner({ text: 'Enter valid rates: day rate 0–100,000, monthly percentage 0–100, and non-negative weekly rates.', kind: 'err' });
      return;
    }
    haptic.impact('medium');
    setSavingBasis(true);
    setBanner(null);
    try {
      const r = await architectApi.putPricingMethodology({
        day_rate: rate, monthly_pct: pct, note: methodology?.note ?? null,
        rules: { ...rules, middle_week_rate: middle, senior_week_rate: senior },
      });
      setMethodology(r.methodology);
      const values = [String(r.methodology.day_rate), String(r.methodology.monthly_pct),
        String(r.methodology.rules?.middle_week_rate ?? DEFAULT_RULES.middle_week_rate),
        String(r.methodology.rules?.senior_week_rate ?? DEFAULT_RULES.senior_week_rate)];
      savedBasisSnapshot.current = JSON.stringify(values);
      setDayRate(values[0]); setMonthlyPct(values[1]); setMiddleRate(values[2]); setSeniorRate(values[3]);
      setBasisOpen(false);
      setBanner({ text: 'Pricing basis saved. It is used for future recommendations.', kind: 'ok' });

    } catch (err) {
      setBanner({ text: (err as Error).message || 'Could not save the pricing basis', kind: 'err' });
    } finally {
      setSavingBasis(false);
    }
  };

  const saveSnapshot = useCallback((draft: PricingSheet) => draft.plan?.workflow_version === 'lato-pricing-v2' || meta?.sheet?.plan?.workflow_version === 'lato-pricing-v2'
    ? architectApi.putPricing(projectId, draft, { review_revision: meta?.review_revision ?? 0, estimate_id: meta?.estimate_id ?? null })
    : architectApi.putPricing(projectId, draft), [projectId, meta?.review_revision, meta?.estimate_id, meta?.sheet?.plan?.workflow_version]);

  const save = useCallback(async () => {
    if (!access.edit || loadedProject.current !== projectId || currentProject.current !== projectId) return;
    if (saving || savingBasis || assistantBusy || recoveryPending.current) return;
    const revision = editRevision.current;
    haptic.impact('medium');
    setSaving(true);
    try {
      const r = await saveSnapshot(sheet);
      haptic.notify('success');
      setMeta(r);
      setRepriceFailure(null);
      setRecoveryError(null);
      const prepared = { ...(r.sheet || EMPTY), plan: r.sheet?.plan ?? plan };
      savedSnapshot.current = JSON.stringify(prepared);
      if (revision === editRevision.current) {
        setSheetState(prepared);
      }
      setBanner({ text: revision !== editRevision.current ? 'Draft saved; newer edits remain. Save again before approval.' : 'Pricing draft saved.', kind: 'ok' });
      try { await onPipelineChange(); } catch { /* The pricing draft was saved successfully. */ }
    } catch (err) {
      haptic.notify('error');
      if ((err as any)?.status === 409 || (err as any)?.status === 428) setSaveConflict(true);
      setBanner({ text: (err as Error).message || 'Could not save prices', kind: 'err' });
    } finally {
      setSaving(false);
    }
  }, [access.edit, saving, savingBasis, assistantBusy, sheet, plan, onPipelineChange, saveSnapshot, projectId]);

  const assignedToMe = !!meta?.assigned_to && meta.assigned_to === user?.id;
  const canReview = meta?.approvals
    ? pricingActor.canApprove
    : !access.configured && !!meta?.assigned_to && (assignedToMe || (!!user?.isRoot && pricingActor.canApprove));
  const setupMissing = sheet.setup.filter(row => !row.price?.trim()).length;
  const v2 = sheet.plan?.workflow_version === 'lato-pricing-v2';
  const reviewConfirmed = PRICING_REVIEW_STEPS.every(section => pricingSectionConfirmed(sheet, section));
  const draftComplete = sheet.setup.length > 0 && (casePricingEnabled(sheet.plan) ? caseSaleTotal(sheet.plan) != null : v2 ? sheet.plan?.sale_price != null : setupMissing === 0) && monthlyRowsReady(sheet.monthly, rules);
  const readyForApproval = access.approve && projectReady && draftComplete && !!meta && canReview &&
    (v2 ? meta.status === 'review' && !dirty : meta.status !== 'approved' || dirty) &&
    !basisDirty && !meta.stale && !busy;
  const readyToSubmit = access.edit && projectReady && v2 && reviewConfirmed && draftComplete && (meta?.can_submit ?? !!meta?.assigned_to) &&
    (meta?.status !== 'review' || dirty) && (meta?.status !== 'approved' || dirty) && !basisDirty && !meta?.stale && !busy;
  const approvePricing = useCallback(async () => {
    if (!readyForApproval || approving || recoveryPending.current) return;
    haptic.impact('medium');
    setApproving(true);
    setBanner(null);
    try {
      if (sheetDirty || !meta?.saved) {
        const revision = editRevision.current;
        const saved = await saveSnapshot(sheetRef.current);
        const prepared = { ...(saved.sheet || EMPTY), plan: saved.sheet?.plan ?? plan };
        savedSnapshot.current = JSON.stringify(prepared);
        setMeta(saved);
        if (revision !== editRevision.current) {
          setBanner({ text: 'New edits arrived while saving. Review them and approve again.', kind: 'err' });
          return;
        }
        setSheetState(prepared);
      }
      const approvalBody = { ...(v2 ? { review_revision: meta?.review_revision } : {}), ...pricingActor.body,
        ...(!meta?.approvals && !access.configured && user?.isRoot && !pricingActor.selected && !!meta?.assigned_to && meta.assigned_to !== user.id ? { override: true } : {}) };
      const result = await (Object.keys(approvalBody).length ? architectApi.approvePricing(projectId, approvalBody) : architectApi.approvePricing(projectId));
      setMeta(result);
      setRepriceFailure(result.reprice_error || null);
      setRecoveryError(null);
      setBanner(result.reprice_error ? null : { text: result.status === 'review'
        ? 'Your approval is recorded. Waiting for the next reviewer.'
        : result.auto_started === 'proposal' ? 'Pricing approved — the proposal is being prepared.' : 'Pricing approved.', kind: 'ok' });
      haptic.notify(result.reprice_error ? 'error' : 'success');
      try { await onPipelineChange(); } catch { /* The approval itself succeeded. */ }
    } catch (error) {
      haptic.notify('error');
      if ((error as any)?.status === 409 || (error as any)?.status === 428) setSaveConflict(true);
      setBanner({ text: approvalErrorText(error), kind: 'err' });
      try { await onPipelineChange(); } catch { /* Keep the server error visible. */ }
    } finally {
      setApproving(false);
    }
  }, [readyForApproval, approving, sheetDirty, meta?.saved, meta?.review_revision, meta?.assigned_to, meta?.approvals, plan, projectId, onPipelineChange, v2, saveSnapshot, pricingActor.body, pricingActor.selected, user?.isRoot, user?.id, access.configured]);

  const submit = async () => {
    if (!readyToSubmit || submitting || !meta) return;
    const revision = editRevision.current;
    setSubmitting(true);
    setBanner(null);
    haptic.impact('medium');
    try {
      let current = meta;
      if (sheetDirty || !meta.saved) {
        current = await saveSnapshot(sheetRef.current);
        const prepared = { ...(current.sheet || EMPTY), plan: current.sheet?.plan ?? plan };
        savedSnapshot.current = JSON.stringify(prepared);
        setMeta(current);
        if (revision !== editRevision.current) {
          setBanner({ text: 'Your draft changed while saving. Review it before sending.', kind: 'err' });
          return;
        }
        setSheetState(prepared);
      }
      const result = await architectApi.submitPricing(projectId, { review_revision: current.review_revision });
      setMeta(result);
      setBanner({ text: `Sent to ${result.assigned_to_name || (result.approvals?.root_only ? 'Root' : 'the assigned approver')}.`, kind: 'ok' });
      haptic.notify('success');
      try { await onPipelineChange(); } catch { /* The server accepted this review request. */ }
    } catch (error) { if ((error as any)?.status === 409 || (error as any)?.status === 428) setSaveConflict(true); setBanner({ text: approvalErrorText(error), kind: 'err' }); haptic.notify('error'); }
    finally { setSubmitting(false); }
  };

  const requestChanges = async () => {
    if (!access.approve || !changesReason.trim() || rejecting || !meta) return;
    setRejecting(true);
    setBanner(null);
    try {
      const result = await architectApi.requestPricingChanges(projectId, { reason: changesReason.trim(), review_revision: meta.review_revision });
      setMeta(result);
      setChangesOpen(false);
      setChangesReason('');
      setBanner({ text: 'Changes requested. The author has been notified.', kind: 'ok' });
      haptic.notify('success');
      try { await onPipelineChange(); } catch { /* The feedback was recorded and the author was notified. */ }
    } catch (error) { if ((error as any)?.status === 409 || (error as any)?.status === 428) setSaveConflict(true); setBanner({ text: approvalErrorText(error), kind: 'err' }); haptic.notify('error'); }
    finally { setRejecting(false); }
  };

  const recoverProposal = async () => {
    if (!user?.isRoot || !repriceFailure || meta?.status !== 'approved' || busy || dirty || recoveryPending.current) return;
    if (!window.confirm('Create a new proposal from the approved estimate and prices? Your manually edited version will stay in history; its edits will not be copied into the new proposal.')) return;
    recoveryPending.current = true;
    setRecoveringProposal(true);
    setRecoveryError(null);
    try {
      await proposalApi.generateProposal(projectId);
    } catch (error) {
      setRecoveryError((error as Error).message || 'Could not start the new proposal. Try again.');
      haptic.notify('error');
      return;
    } finally {
      recoveryPending.current = false;
      setRecoveringProposal(false);
    }
    setRepriceFailure(null);
    setBanner({ text: 'The new proposal is being prepared. Your edited version stays in history.', kind: 'ok' });
    haptic.notify('success');
    try { await onPipelineChange(); } catch { /* The generation run has started. */ }
  };

  useMainButton({ text: 'Approve pricing', visible: false, onClick: () => {} });

  if (!active || !access.view) return null;

  return (
    <div className="tma-doc tma-pricing tma-pr stage-mini-surface" ref={rootRef}>
      <StageHeader title="Pricing" status={!projectReady ? (loading ? 'Loading' : 'Unavailable') : meta?.status === 'approved' && !dirty ? 'Approved' : dirty ? 'Unsaved changes' : meta?.status === 'review' ? 'In review' : 'Draft'} actions={[
        ...(access.edit ? [{ id: 'monthly-help', label: 'Ask Lato AI', icon: <SlidersHorizontal size={17}/>, disabled: !projectReady || loading || !sheet.setup.length, onClick: () => { setStep('monthly'); setMonthlyVisited(true); setSuggestionsOpen(true); haptic.select(); } }] : []),
        ...(user?.isRoot ? [{ id: 'rates', label: 'Pricing rates', icon: <SlidersHorizontal size={17}/>, disabled: !projectReady, onClick: () => setSettingsOpen(true) }] : []),
        ...(user?.isRoot && pricingActor.choices.length && meta?.status !== 'approved' ? [{ id: 'approver', label: 'Approve as', icon: <UserRound size={17}/>, disabled: !projectReady || busy || dirty, onClick: () => setApprovalSettingsOpen(true) }] : []),
        ...(access.approve && projectReady && meta?.status === 'review' && canReview && !dirty ? [{ id: 'changes', label: 'Request changes', icon: <MessageCircle size={17}/>, disabled: busy, onClick: () => setChangesOpen(true) }] : []),
      ]}/>
      {projectReady && repriceFailure ? <div className="tma-banner err" role="alert">
        <p>Pricing approved. Your edited proposal stays in history. A new version can be created from the approved estimate and prices.</p>
        {recoveryError ? <p>{recoveryError}</p> : null}
        {user?.isRoot ? <button type="button" className="tma-btn tma-btn-primary" disabled={busy || dirty || meta?.status !== 'approved'} onClick={() => void recoverProposal()}>{recoveringProposal ? 'Starting…' : 'Create new proposal'}</button> : null}
      </div> : null}
      {projectReady && saveConflict ? <div className="tma-banner warn pricing-conflict" role="status"><p>{confirmReload ? 'Discard your unsaved pricing edits and load current pricing?' : 'Current pricing changed. Your edits are kept here.'}</p><div>{confirmReload ? <button type="button" className="tma-btn" disabled={busy} onClick={() => setConfirmReload(false)}>Keep editing</button> : null}<button type="button" className="tma-btn" disabled={busy} onClick={() => { if (dirty && !confirmReload) setConfirmReload(true); else void load(); }}>{confirmReload ? 'Discard edits and reload' : 'Reload current pricing'}</button></div></div> : null}
      {banner && !(projectReady && (settingsOpen || (access.approve && changesOpen))) ? <PricingNotice text={banner.text} kind={banner.kind} onDismiss={() => setBanner(null)}/> : null}
      {loading ? (
        <div className="tma-card tma-card-skel"><div className="tma-skel tma-skel-name" /><div className="tma-skel tma-skel-meta" /></div>
      ) : null}
      {projectReady && !loading && meta && !sheet.setup.length ? (
        <div className="tma-empty">
          <div className="tma-empty-emoji">💰</div>
          <div className="tma-empty-title">No price rows yet</div>
          <div className="tma-empty-sub">The rows come from the approved estimate — one per case. Approve an estimate first and they appear here.</div>
        </div>
      ) : null}
      {projectReady && !loading && meta?.stale ? <div className="tma-banner warn">The estimate changed after these prices were set — check the rows.</div> : null}
      {projectReady && !loading && meta?.rejection_reason ? <div className="pricing-returned" role="status"><strong>Changes requested{meta.rejected_by_name ? ` by ${meta.rejected_by_name}` : ''}</strong><p>{meta.rejection_reason}</p></div> : null}

      {projectReady && !loading && sheet.setup.length ? <PricingReviewFlow sheet={sheet} rules={rules} step={step}
        onStepChange={next => { haptic.select(); setStep(next); if (next === 'monthly') setMonthlyVisited(true); }}
        onChange={setSheet} disabled={busy || !access.edit} finalPriceView={!user?.isRoot}
        approved={meta?.status === 'approved' && !dirty}
        sourceOpen={sourceOpen || !!pickingComment}
        caseFocusPath={jumpAnchor?.path} caseFocusToken={jumpAnchor}
        caseAttributes={(row, index) => ({ 'data-tma-pr-anchor': `setup.${index}`, 'data-tma-pr-label': row.case, tabIndex: -1 }) as React.HTMLAttributes<HTMLDivElement>}
        caseDetails={(row, index) => commentOn(`setup.${index}`, row.case, `${row.case}: ${row.blurb || ''}`)}
        settings={user?.isRoot ? <button type="button" className="tma-pr-settings-open" onClick={() => setSettingsOpen(true)}><SlidersHorizontal size={16} /> Pricing rates</button> : null}
      >
      {projectReady && !loading && sheet.setup.length && monthlyVisited ? <MonthlyServicesEditor key={projectId}
        rows={sheet.monthly} rules={rules} disabled={!access.edit || approving || submitting || rejecting || recoveringProposal}
        caseNames={sheet.setup.map(row => row.case)} onChange={setMonthly} onRemove={removeMonthly} onAdd={addCustom}
        rowAttributes={(row, index) => ({ 'data-tma-pr-anchor': `monthly.${index}`, 'data-tma-pr-label': row.label,
          tabIndex: -1, className: highlightPath === `monthly.${index}` ? 'tma-pr-anchor-highlight' : '' })}
        rowAction={(row, index) => commentOn(`monthly.${index}`, row.label || `Service ${index + 1}`, `${row.label}: ${row.description || ''}`)} /> : null}
      {access.edit && projectReady && !loading && loadedProject.current === projectId && sheet.setup.length && monthlyVisited ? <div hidden={step !== 'monthly'}>
        <TmaMonthlyRecommendation key={`${projectId}:${monthlySourceKey}`} projectId={projectId} sheet={sheet}
          expanded={!sheet.monthly.length || suggestionsOpen} onExpand={() => setSuggestionsOpen(true)} onCollapse={sheet.monthly.length ? () => setSuggestionsOpen(false) : undefined}
          disabled={saving || savingBasis} onBusyChange={setAssistantBusy} onApply={applyRecommendation} />
      </div> : null}
      </PricingReviewFlow> : null}

      {access.edit ? <StageAiButton mini busy={assistantBusy} disabled={!projectReady || loading || !sheet.setup.length} onClick={() => {
        setStep('monthly'); setMonthlyVisited(true); setSuggestionsOpen(true); haptic.select();
        window.setTimeout(() => rootRef.current?.querySelector('.tma-mr')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
      }}/> : null}
      {projectReady ? <StageFooter className="stage-mini-footer" stage="pricing" pending={meta?.status !== 'approved' || dirty} status={meta?.approvals} userId={user?.id} count={meta?.approvals ? undefined : { given: meta?.status === 'approved' && !dirty ? 1 : 0, required: 1 }} title={meta?.status === 'approved' && !dirty ? 'Pricing approved' : meta?.status === 'review' && !dirty ? 'Ready for review' : 'Pricing draft'} hint={!meta?.approvals && meta?.status === 'review' && !assignedToMe ? `Waiting for ${meta.assigned_to_name || 'assigned approver'}` : undefined}>
        {access.edit && (sheetDirty || (!meta?.saved && sheet.setup.length > 0)) ? <button type="button" className="arch-btn arch-btn-outline" disabled={busy} onClick={() => void save()}><SaveIcon size={15}/>{saving ? 'Saving…' : 'Save draft'}</button> : null}
        {readyToSubmit || readyForApproval || submitting || approving ? <button type="button" className="arch-btn arch-btn-primary" disabled={busy || commentsOpen || settingsOpen || basisOpen || changesOpen || approvalSettingsOpen} onClick={() => void (readyToSubmit ? submit() : approvePricing())}>{submitting ? 'Sending…' : approving ? 'Approving…' : readyToSubmit ? 'Send for approval' : architectureReviewView(meta?.approvals, 'pricing')?.action || 'Approve pricing'}</button> : null}
      </StageFooter> : null}

      <Sheet open={!!user?.isRoot && projectReady && approvalSettingsOpen} onClose={() => setApprovalSettingsOpen(false)} title="Approve as" className="tma-pr tma-pr-settings"
        headerAction={<button type="button" className="tma-pr-settings-done" onClick={() => setApprovalSettingsOpen(false)}>Done</button>}>
        <ApproveAsSelector actor={pricingActor} disabled={busy || dirty}/>
      </Sheet>
      <Sheet open={access.approve && projectReady && changesOpen} onClose={() => { if (!rejecting) setChangesOpen(false); }} title="Request changes" className="pricing-changes-sheet">
        {banner && changesOpen ? <PricingNotice inline text={banner.text} kind={banner.kind} onDismiss={() => setBanner(null)}/> : null}
        <label>What needs to change?<textarea className="tma-input" rows={4} maxLength={2000} value={changesReason} disabled={rejecting} onChange={event => setChangesReason(event.target.value)} placeholder="Explain what the author should adjust." /></label>
        <button type="button" className="tma-btn tma-btn-primary" disabled={rejecting || !changesReason.trim()} onClick={() => void requestChanges()}>{rejecting ? 'Sending…' : 'Send feedback'}</button>
      </Sheet>
      <Sheet open={projectReady && settingsOpen} onClose={() => { cancelBasis(); setSettingsOpen(false); }} title="Pricing rates" className="tma-pr tma-pr-settings"
        headerAction={<button type="button" className="tma-pr-settings-done" disabled={savingBasis} onClick={() => { cancelBasis(); setSettingsOpen(false); }}>Done</button>}>
          {banner && settingsOpen ? <PricingNotice inline text={banner.text} kind={banner.kind} onDismiss={() => setBanner(null)}/> : null}
          <div className="tma-pr-basis-card">
            {!basisOpen ? <button type="button" className="tma-pr-basis-open" disabled={!user?.isRoot} onClick={() => { haptic.impact('light'); setBasisOpen(true); }}>
              <span><strong>Pricing basis</strong><small>${methodology?.day_rate ?? '—'}/day · Middle ${rules.middle_week_rate} · Senior ${rules.senior_week_rate} / week</small></span>
              <span aria-hidden="true">{user?.isRoot ? 'Edit ›' : ''}</span>
            </button> : <div className="tma-pr-basis-form">
              <div><strong>Edit pricing basis</strong><p>Shared rates for all projects.</p></div>
              <div className="tma-pr-basis-fields">
                <label>Day rate<input className="tma-input" inputMode="decimal" value={dayRate} disabled={savingBasis} onChange={e => editBasis(setDayRate, e.target.value)} /></label>
                <label>Middle $/week<input className="tma-input" inputMode="decimal" value={middleRate} disabled={savingBasis} onChange={e => editBasis(setMiddleRate, e.target.value)} /></label>
                <label>Senior $/week<input className="tma-input" inputMode="decimal" value={seniorRate} disabled={savingBasis} onChange={e => editBasis(setSeniorRate, e.target.value)} /></label>
              </div>
              <div className="tma-pr-basis-actions"><button type="button" className="tma-btn" disabled={savingBasis} onClick={cancelBasis}>Cancel</button><button type="button" className="tma-btn tma-btn-primary" disabled={savingBasis || assistantBusy || !basisDirty} onClick={() => void saveBasis()}>{savingBasis ? 'Saving…' : 'Save basis'}</button></div>
            </div>}
          </div>
      </Sheet>
      <div className="tma-mb-spacer" />
    </div>
  );
}
